import React, { useEffect, useState } from "react"
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Row,
} from "reactstrap"
import { Form, ErrorMessage, Field, Formik } from "formik"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { showAlert } from "store/reducers/alertReducer"
import Spinners from "components/Shared/Spinners"
import {
  addRegion,
  getRegionDetails,
  updateRegion,
} from "store/actions/regionAction"
import Select from "react-select"
import { getCountryList } from "store/actions/countriesAction"
import { addRegionSchema } from "components/Common/validation"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"

const initialValues = {
  name: "",
  nameAr: "",
  country: "",
  status: "",
}

const AddRegion = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = `${
    mode === "add" ? t("Add") : t("Edit")
  } ${"Region | Namq"}`
  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")

  const { userRole, accountType } = useSelector(state => state.Login)

  useEffect(() => {
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      25,
      "region_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      25,
      "region_3",
      accountType
    )
    if (mode === "add") {
      if (!isPermissionCreate) {
        navigate("/dashboard")
      }
    } else if (mode === "edit") {
      if (!isPermissionEdit) {
        navigate("/dashboard")
      }
    }
  }, [])

  const { loading, isLoading, regionDetails } = useSelector(
    state => state.Region
  )
  const { countryList } = useSelector(state => state.Countries)

  const [formValues, setFormValues] = useState(initialValues)

  useEffect(() => {
    fetchCountryList()
  }, [])

  const fetchCountryList = () => {
    dispatch(getCountryList())
  }

  useEffect(() => {
    if (mode === "edit") {
      fetchRegionDetails()
    }
  }, [])

  useEffect(() => {
    if (regionDetails && !loading && mode === "edit") {
      const { name, nameAr, country, status } = regionDetails
      const updatedCountry = {
        label: language === "sa" ? country?.nameAr : country?.name,
        value: country?._id,
        status,
      }
      setFormValues({
        ...formValues,
        name,
        nameAr,
        country: updatedCountry,
        status,
      })
    }
  }, [regionDetails])

  const fetchRegionDetails = () => {
    const payload = { id }
    dispatch(getRegionDetails(payload))
  }

  const handleOnSubmit = values => {
    const { name, nameAr, country, status } = values
    const countryId = country?.value
    const payload = {
      name,
      nameAr,
      country: countryId,
    }
    if (mode === "add") {
      dispatch(addRegion(payload))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate("/regions")
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    } else if (mode === "edit") {
      const editPayload = {
        ...payload,
        id,
        status: Number(status),
      }
      dispatch(updateRegion(editPayload))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    }
  }
  if (mode === "edit" && loading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle className="mb-2 d-flex align-items-center">
                  <BackButton />
                  {mode === "add" ? t("Add") : t("Edit")} {t("Region")}
                </CardTitle>
                <Formik
                  initialValues={formValues}
                  enableReinitialize={true}
                  validationSchema={addRegionSchema}
                  onSubmit={handleOnSubmit}
                >
                  {({ values, setFieldValue, errors, touched }) => (
                    <Form>
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="name">
                              <span className="text-danger">*</span>{" "}
                              {t("Name(English)")}
                            </label>
                            <Field
                              type="text"
                              id="name"
                              name="name"
                              placeholder={t("Name(English)")}
                              className={`form-control ${
                                touched.name && errors.name ? "is-invalid" : ""
                              }`}
                            />
                            <TextError name="name" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="name">
                              <span className="text-danger">*</span>{" "}
                              {t("Name(عربي)")}
                            </label>
                            <Field
                              type="text"
                              id="nameAr"
                              name="nameAr"
                              placeholder={t("Name(عربي)")}
                              className={`form-control ${
                                touched.nameAr && errors.nameAr
                                  ? "is-invalid"
                                  : ""
                              }`}
                              style={{ direction: "rtl" }}
                            />
                            <TextError name="nameAr" />
                          </div>
                        </div>

                        <div className="col-lg-6">
                          <div className="">
                            <label htmlFor="country">
                              <span className="text-danger">*</span>{" "}
                              {t("Select Country")}
                            </label>
                            <Select
                              value={values.country}
                              onChange={item => {
                                setFieldValue("country", item)
                              }}
                              options={countryList?.map(item => ({
                                label:
                                  language === "sa" ? item.nameAr : item.name,
                                value: item._id,
                              }))}
                              className={`select2-selection ${
                                touched.country && errors.country
                                  ? "danger_color_border"
                                  : ""
                              } `}
                              placeholder={t("Select Country")}
                            />
                            <TextError name="country" />
                          </div>
                        </div>

                        <div className="col-12 d-flex justify-content-end mt-3 mb-3">
                          <Button
                            type="button"
                            className="mx-2"
                            color="secondary"
                            onClick={() => navigate(-1)}
                          >
                            {t("Cancel")}
                          </Button>
                          <Button
                            type="submit"
                            color="success"
                            disabled={isLoading}
                            className="w_fit_content"
                          >
                            {mode === "add" ? t("Add") : t("Save")}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default AddRegion
